/**
 * @module useAirline
 */
import { useCallback } from 'react'
import {
  useCreateAirlineMutation,
  useUpdateAirlineMutation,
  useDeleteAirlineMutation,
  useImportAirlinesMutation,
  useUpdateStatusMutation,
  useAddDestinationMutation,
  useRemoveDestinationMutation,
  useAddHubAirportMutation,
  useRemoveHubAirportMutation,
  useUpdateFleetSizeMutation,
  useUpdateContactInfoMutation,
  useUpdateSocialMediaMutation,
  useUpdateRegulatoryInfoMutation,
  useUpdateFinancialInfoMutation,
  useGetAnnualGrowthMutation,
  useGetCertificateStatusMutation,
  useGetHubCountMutation,
  useGetDestinationCountMutation,
  useGetAirlinesMutation,
  useGetAirlineMutation,
  useFindByIataCodeMutation,
} from '../services/airline.service'

export const useAirline = () => {
  // Basic CRUD operations
  const [executeGetAirlines, { isLoading: isGetAirlinesLoading }] = useGetAirlinesMutation()
  const [executeGetAirline, { isLoading: isGetAirlineLoading }] = useGetAirlineMutation()
  const [executeCreateAirline, { isLoading: isCreateAirlineLoading }] = useCreateAirlineMutation()
  const [executeUpdateAirline, { isLoading: isUpdateAirlineLoading }] = useUpdateAirlineMutation()
  const [executeDeleteAirline, { isLoading: isDeleteAirlineLoading }] = useDeleteAirlineMutation()
  const [executeImportAirlines, { isLoading: isImportAirlinesLoading }] =
    useImportAirlinesMutation()

  // Additional operation routes
  const [executeUpdateStatus, { isLoading: isUpdateStatusLoading }] = useUpdateStatusMutation()
  const [executeAddDestination, { isLoading: isAddDestinationLoading }] =
    useAddDestinationMutation()
  const [executeRemoveDestination, { isLoading: isRemoveDestinationLoading }] =
    useRemoveDestinationMutation()
  const [executeAddHubAirport, { isLoading: isAddHubAirportLoading }] = useAddHubAirportMutation()
  const [executeRemoveHubAirport, { isLoading: isRemoveHubAirportLoading }] =
    useRemoveHubAirportMutation()
  const [executeUpdateFleetSize, { isLoading: isUpdateFleetSizeLoading }] =
    useUpdateFleetSizeMutation()
  const [executeUpdateContactInfo, { isLoading: isUpdateContactInfoLoading }] =
    useUpdateContactInfoMutation()
  const [executeUpdateSocialMedia, { isLoading: isUpdateSocialMediaLoading }] =
    useUpdateSocialMediaMutation()
  const [executeUpdateRegulatoryInfo, { isLoading: isUpdateRegulatoryInfoLoading }] =
    useUpdateRegulatoryInfoMutation()
  const [executeUpdateFinancialInfo, { isLoading: isUpdateFinancialInfoLoading }] =
    useUpdateFinancialInfoMutation()

  // Analytics routes
  const [executeGetAnnualGrowth, { isLoading: isGetAnnualGrowthLoading }] =
    useGetAnnualGrowthMutation()
  const [executeGetCertificateStatus, { isLoading: isGetCertificateStatusLoading }] =
    useGetCertificateStatusMutation()
  const [executeGetHubCount, { isLoading: isGetHubCountLoading }] = useGetHubCountMutation()
  const [executeGetDestinationCount, { isLoading: isGetDestinationCountLoading }] =
    useGetDestinationCountMutation()

  // Search routes
  const [executeFindByIataCode, { isLoading: isFindByIataCodeLoading }] =
    useFindByIataCodeMutation()

  // Basic CRUD async functions
  const getAirlinesAsync = useCallback(async () => {
    if (isGetAirlinesLoading) {
      return
    }
    try {
      const response = await executeGetAirlines().unwrap()
      return response.data.airlines || []
    } catch (error) {
      console.error('#getAirlinesAsync: Error fetching airlines', error)
      throw error
    }
  }, [executeGetAirlines, isGetAirlinesLoading])

  const getAirlineAsync = useCallback(
    async (airlineId) => {
      if (isGetAirlineLoading) {
        return
      }
      try {
        const response = await executeGetAirline(airlineId).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#getAirlineAsync: Error fetching airline', error)
        throw error
      }
    },
    [executeGetAirline, isGetAirlineLoading],
  )

  const createAirlineAsync = useCallback(
    async (airline) => {
      if (isCreateAirlineLoading) {
        return
      }
      try {
        const response = await executeCreateAirline(airline).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#createAirlineAsync: Error creating airline', error)
        throw error
      }
    },
    [executeCreateAirline, isCreateAirlineLoading],
  )

  const updateAirlineAsync = useCallback(
    async (airlineId, updates) => {
      if (isUpdateAirlineLoading) {
        return
      }
      try {
        const response = await executeUpdateAirline({ airlineId, ...updates }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateAirlineAsync: Error updating airline', error)
        throw error
      }
    },
    [executeUpdateAirline, isUpdateAirlineLoading],
  )

  const deleteAirlineAsync = useCallback(
    async (airlineId) => {
      if (isDeleteAirlineLoading) {
        return
      }
      try {
        const response = await executeDeleteAirline(airlineId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteAirlineAsync: Error deleting airline', error)
        throw error
      }
    },
    [executeDeleteAirline, isDeleteAirlineLoading],
  )

  const importAirlinesAsync = useCallback(
    async (airlinesData) => {
      if (isImportAirlinesLoading) {
        return
      }
      try {
        const response = await executeImportAirlines(airlinesData).unwrap()
        return response.data
      } catch (error) {
        console.error('#importAirlinesAsync: Error importing airlines', error)
        throw error
      }
    },
    [executeImportAirlines, isImportAirlinesLoading],
  )

  // Additional operation async functions
  const updateStatusAsync = useCallback(
    async (airlineId, status) => {
      if (isUpdateStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateStatus({ airlineId, status }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateStatusAsync: Error updating airline status', error)
        throw error
      }
    },
    [executeUpdateStatus, isUpdateStatusLoading],
  )

  const addDestinationAsync = useCallback(
    async (airlineId, destination) => {
      if (isAddDestinationLoading) {
        return
      }
      try {
        const response = await executeAddDestination({ airlineId, destination }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#addDestinationAsync: Error adding destination', error)
        throw error
      }
    },
    [executeAddDestination, isAddDestinationLoading],
  )

  const removeDestinationAsync = useCallback(
    async (airlineId, destination) => {
      if (isRemoveDestinationLoading) {
        return
      }
      try {
        const response = await executeRemoveDestination({ airlineId, destination }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#removeDestinationAsync: Error removing destination', error)
        throw error
      }
    },
    [executeRemoveDestination, isRemoveDestinationLoading],
  )

  const addHubAirportAsync = useCallback(
    async (airlineId, hubAirport) => {
      if (isAddHubAirportLoading) {
        return
      }
      try {
        const response = await executeAddHubAirport({ airlineId, hubAirport }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#addHubAirportAsync: Error adding hub airport', error)
        throw error
      }
    },
    [executeAddHubAirport, isAddHubAirportLoading],
  )

  const removeHubAirportAsync = useCallback(
    async (airlineId, hubAirport) => {
      if (isRemoveHubAirportLoading) {
        return
      }
      try {
        const response = await executeRemoveHubAirport({ airlineId, hubAirport }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#removeHubAirportAsync: Error removing hub airport', error)
        throw error
      }
    },
    [executeRemoveHubAirport, isRemoveHubAirportLoading],
  )

  const updateFleetSizeAsync = useCallback(
    async (airlineId, fleetSize) => {
      if (isUpdateFleetSizeLoading) {
        return
      }
      try {
        const response = await executeUpdateFleetSize({ airlineId, fleetSize }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateFleetSizeAsync: Error updating fleet size', error)
        throw error
      }
    },
    [executeUpdateFleetSize, isUpdateFleetSizeLoading],
  )

  const updateContactInfoAsync = useCallback(
    async (airlineId, contactInfo) => {
      if (isUpdateContactInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateContactInfo({ airlineId, contactInfo }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateContactInfoAsync: Error updating contact info', error)
        throw error
      }
    },
    [executeUpdateContactInfo, isUpdateContactInfoLoading],
  )

  const updateSocialMediaAsync = useCallback(
    async (airlineId, socialMedia) => {
      if (isUpdateSocialMediaLoading) {
        return
      }
      try {
        const response = await executeUpdateSocialMedia({ airlineId, socialMedia }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateSocialMediaAsync: Error updating social media', error)
        throw error
      }
    },
    [executeUpdateSocialMedia, isUpdateSocialMediaLoading],
  )

  const updateRegulatoryInfoAsync = useCallback(
    async (airlineId, regulatoryInfo) => {
      if (isUpdateRegulatoryInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateRegulatoryInfo({ airlineId, regulatoryInfo }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateRegulatoryInfoAsync: Error updating regulatory info', error)
        throw error
      }
    },
    [executeUpdateRegulatoryInfo, isUpdateRegulatoryInfoLoading],
  )

  const updateFinancialInfoAsync = useCallback(
    async (airlineId, financialInfo) => {
      if (isUpdateFinancialInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateFinancialInfo({ airlineId, financialInfo }).unwrap()
        return response.data.airline
      } catch (error) {
        console.error('#updateFinancialInfoAsync: Error updating financial info', error)
        throw error
      }
    },
    [executeUpdateFinancialInfo, isUpdateFinancialInfoLoading],
  )

  // Analytics async functions
  const getAnnualGrowthAsync = useCallback(
    async (airlineId) => {
      if (isGetAnnualGrowthLoading) {
        return
      }
      try {
        const response = await executeGetAnnualGrowth(airlineId).unwrap()
        return response.data?.growth
      } catch (error) {
        console.error('#getAnnualGrowthAsync: Error fetching annual growth', error)
        throw error
      }
    },
    [executeGetAnnualGrowth, isGetAnnualGrowthLoading],
  )

  const getCertificateStatusAsync = useCallback(
    async (airlineId) => {
      if (isGetCertificateStatusLoading) {
        return
      }
      try {
        const response = await executeGetCertificateStatus(airlineId).unwrap()
        return response.data?.certificateStatus
      } catch (error) {
        console.error('#getCertificateStatusAsync: Error fetching certificate status', error)
        throw error
      }
    },
    [executeGetCertificateStatus, isGetCertificateStatusLoading],
  )

  const getHubCountAsync = useCallback(
    async (airlineId) => {
      if (isGetHubCountLoading) {
        return
      }
      try {
        const response = await executeGetHubCount(airlineId).unwrap()
        return response.data?.hubCount
      } catch (error) {
        console.error('#getHubCountAsync: Error fetching hub count', error)
        throw error
      }
    },
    [executeGetHubCount, isGetHubCountLoading],
  )

  const getDestinationCountAsync = useCallback(
    async (airlineId) => {
      if (isGetDestinationCountLoading) {
        return
      }
      try {
        const response = await executeGetDestinationCount(airlineId).unwrap()
        return response.data?.destinationCount
      } catch (error) {
        console.error('#getDestinationCountAsync: Error fetching destination count', error)
        throw error
      }
    },
    [executeGetDestinationCount, isGetDestinationCountLoading],
  )

  // Search async functions
  const findByIataCodeAsync = useCallback(
    async (iataCode) => {
      if (isFindByIataCodeLoading) {
        return
      }
      try {
        const response = await executeFindByIataCode(iataCode).unwrap()
        return response.data?.airline
      } catch (error) {
        console.error('#findByIataCodeAsync: Error finding airline by IATA code', error)
        throw error
      }
    },
    [executeFindByIataCode, isFindByIataCodeLoading],
  )

  return {
    // Basic CRUD functions
    getAirlinesAsync,
    getAirlineAsync,
    createAirlineAsync,
    updateAirlineAsync,
    deleteAirlineAsync,
    importAirlinesAsync,

    // Additional operation functions
    updateStatusAsync,
    addDestinationAsync,
    removeDestinationAsync,
    addHubAirportAsync,
    removeHubAirportAsync,
    updateFleetSizeAsync,
    updateContactInfoAsync,
    updateSocialMediaAsync,
    updateRegulatoryInfoAsync,
    updateFinancialInfoAsync,

    // Analytics functions
    getAnnualGrowthAsync,
    getCertificateStatusAsync,
    getHubCountAsync,
    getDestinationCountAsync,

    // Search functions
    findByIataCodeAsync,

    // Loading states
    isGetAirlinesLoading,
    isGetAirlineLoading,
    isCreateAirlineLoading,
    isUpdateAirlineLoading,
    isDeleteAirlineLoading,
    isImportAirlinesLoading,
    isUpdateStatusLoading,
    isAddDestinationLoading,
    isRemoveDestinationLoading,
    isAddHubAirportLoading,
    isRemoveHubAirportLoading,
    isUpdateFleetSizeLoading,
    isUpdateContactInfoLoading,
    isUpdateSocialMediaLoading,
    isUpdateRegulatoryInfoLoading,
    isUpdateFinancialInfoLoading,
    isGetAnnualGrowthLoading,
    isGetCertificateStatusLoading,
    isGetHubCountLoading,
    isGetDestinationCountLoading,
    isFindByIataCodeLoading,
  }
}
